import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconBack, IconCheck, IconClose, IconEye, IconEyeOff, IconForward, IconPlay } from '../components/Icon';
import { remove, setCaption, setHidden, useMedia, visibleTo } from '../lib/media';
import { useSession } from '../lib/session';
import { useSchedule } from '../lib/content';
import { relativeUploadTime } from '../lib/time';
import NotFound from './NotFound';

/**
 * Artboard 1j — the only place a video plays (D26), and the only place a photo
 * is moderated (D40).
 *
 * It is dark in both themes on purpose: the photograph is the light source, and
 * a cream surround under a reception photo washes it out.
 */
export default function PhotoDetail() {
  const { photoId } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const media = useMedia();
  const schedule = useSchedule();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [playing, setPlaying] = useState(false);
  const touchStart = useRef<number | null>(null);

  const items = useMemo(() => visibleTo(media, session), [media, session]);
  const index = items.findIndex((it) => it.id === photoId);
  const item = index >= 0 ? items[index] : undefined;

  const go = useMemo(() => (delta: number) => {
    const next = items[index + delta];
    if (next) navigate(`/gallery/${next.id}`, { replace: true });
  }, [items, index, navigate]);

  useEffect(() => {
    setEditing(false);
    setConfirmingDelete(false);
    setPlaying(false);
  }, [photoId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'Escape') navigate('/gallery');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, navigate]);

  if (!item) return <NotFound />;

  const mine = item.uploaderId === session?.userId;
  const eventName = schedule.data?.events.find((e) => e.id === item.eventId)?.name;
  const dotWindow = items.slice(Math.max(0, index - 2), Math.max(0, index - 2) + 5);

  function saveCaption() {
    setCaption(item!.id, draft);
    setEditing(false);
  }

  return (
    <div
      className="viewer"
      onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
        touchStart.current = null;
      }}
    >
      <div className="viewer-bar">
        <button type="button" className="viewer-btn" onClick={() => navigate('/gallery')} aria-label="Back to the gallery">
          <IconBack size={17} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="who">{item.uploaderName}</div>
          <div className="when">
            {relativeUploadTime(item.createdAt)}{eventName ? ` · ${eventName}` : ''}
          </div>
        </div>
        {item.hidden && (
          <span className="tag" style={{ background: 'var(--color-accent-600)', color: '#fff' }}>Hidden</span>
        )}
        <span className="tag" style={{ background: 'color-mix(in srgb, #f9f4ed 14%, transparent)', color: 'inherit' }}>
          {index + 1} of {items.length}
        </span>
      </div>

      <div className="viewer-stage">
        {item.kind === 'video' ? (
          playing ? (
            <video src={item.src} controls autoPlay playsInline />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', width: '100%', height: '100%' }}
              aria-label="Play video"
            >
              <img src={item.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              <span className="tile-play"><span><IconPlay size={20} /></span></span>
            </button>
          )
        ) : (
          <img src={item.src} alt={item.caption ?? `Photo from ${item.uploaderName}`} />
        )}

        {index > 0 && <button type="button" className="viewer-nav prev" onClick={() => go(-1)}>Previous</button>}
        {index < items.length - 1 && <button type="button" className="viewer-nav next" onClick={() => go(1)}>Next</button>}
      </div>

      <div className="viewer-dots" aria-hidden="true">
        {dotWindow.map((d) => <i key={d.id} className={d.id === item.id ? 'on' : ''} />)}
      </div>

      <div className="viewer-foot">
        {editing ? (
          <div className="viewer-caption">
            <input
              className="input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCaption(); }}
              style={{ background: 'color-mix(in srgb, #f9f4ed 12%, transparent)', color: 'inherit', borderColor: 'transparent' }}
              placeholder="Say what was happening"
            />
            <button type="button" className="viewer-btn" onClick={saveCaption} aria-label="Save caption"><IconCheck size={17} /></button>
            <button type="button" className="viewer-btn" onClick={() => setEditing(false)} aria-label="Cancel"><IconClose size={17} /></button>
          </div>
        ) : (
          (item.caption || mine) && (
            <div className="viewer-caption">
              <p style={{ opacity: item.caption ? 1 : 0.5 }}>{item.caption ?? 'No caption'}</p>
              {mine && (
                <button
                  type="button" className="btn btn-ghost"
                  style={{ color: 'var(--color-accent-400)', minHeight: 32, fontSize: 12.5 }}
                  onClick={() => { setDraft(item.caption ?? ''); setEditing(true); }}
                >
                  Edit
                </button>
              )}
            </div>
          )
        )}

        {(mine || session?.isAdmin) && (
          <div className="viewer-actions">
            {mine && (
              confirmingDelete ? (
                <>
                  <button type="button" className="btn" style={{ flex: 1, background: 'var(--color-accent-600)', color: '#fff' }} onClick={() => { remove(item.id); navigate('/gallery', { replace: true }); }}>
                    Yes, delete it
                  </button>
                  <button type="button" className="btn" style={{ flex: 'none', paddingInline: 18 }} onClick={() => setConfirmingDelete(false)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button type="button" className="btn" style={{ flex: 1 }} onClick={() => setConfirmingDelete(true)}>
                  Delete this photo
                </button>
              )
            )}
            {session?.isAdmin && !confirmingDelete && (
              /* Reversible, no confirmation, and the state is obvious at a
                 glance. Admins cannot delete — that stays with the owner. */
              <button
                type="button" className="btn"
                style={{ flex: mine ? 'none' : 1, paddingInline: 18, gap: 8 }}
                onClick={() => setHidden(item.id, !item.hidden)}
              >
                {item.hidden ? <><IconEye size={16} /> Show again</> : <><IconEyeOff size={16} /> Hide</>}
              </button>
            )}
          </div>
        )}

        {index < items.length - 1 && (
          <button
            type="button" className="btn btn-ghost"
            style={{ color: 'var(--color-accent-400)', marginTop: 4 }}
            onClick={() => go(1)}
          >
            Next photo <IconForward size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
